import { type JSX, type ReactNode, useEffect, useRef, useState } from "react"

// The ⋯ button and its dropdown. Generic on purpose: it owns opening, closing,
// outside-click, Escape and focus return, and nothing else. What the items do
// is the caller's business.
//
// One implementation for both places a workspace menu appears — the row on
// /user/workspaces and the rail on the workspace page — which is why it takes
// items as data rather than rendering anything workspace-specific itself.
export type RowMenuItem = {
  id: string
  label: string
  onSelect: () => void
  danger?: boolean
}

export function RowMenu({
  label,
  items,
  children,
}: {
  // What a screen reader announces for the dots, e.g. "Actions for Protein folding".
  label: string
  items: RowMenuItem[]
  // Dialogs the items open. Rendered outside the dropdown so closing the menu
  // — which selecting an item does — does not unmount the thing it opened.
  children?: ReactNode
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    function onDocumentClick(event: MouseEvent): void {
      const target = event.target
      if (target instanceof Node && rootRef.current?.contains(target)) return
      setOpen(false)
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== "Escape") return
      setOpen(false)
      // Escape should leave you where you started, not at the top of the page.
      toggleRef.current?.focus()
    }

    document.addEventListener("click", onDocumentClick)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("click", onDocumentClick)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  return (
    <div className="row-menu" ref={rootRef}>
      <button
        type="button"
        ref={toggleRef}
        className="row-menu__toggle"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {/* Three drawn dots: ::before, this span, ::after. */}
        <span className="row-menu__dot" />
      </button>

      {open && (
        <div className="row-menu__dropdown" role="menu">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className={`row-menu__item${item.danger ? " row-menu__item--danger" : ""}`}
              onClick={() => {
                setOpen(false)
                item.onSelect()
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {children}
    </div>
  )
}
